"use client";

import { Container } from "@/components/container";
import { MotionDiv, useInView } from "@/lib/framer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Check, Clock, ArrowRight } from "lucide-react";
import React, { useRef } from "react";
import clsx from "clsx";
import { Link } from "@inertiajs/react";
import { Badge } from "@/components/ui/badge";
import ArrowBottom from "@/components/ui/arrow-bottom";

interface PricingPlan {
  title: string;
  price: string;
  description: string;
  features: string[];
  isLifetime?: boolean;
}

interface PricingCardProps {
  plan: PricingPlan;
  delay: number;
  className?: string;
  isRecommended?: boolean;
  isYearly: boolean;
  // eslint-disable-next-line no-unused-vars
  onBillingChange?: (isYearly: boolean) => void;
  showBillingToggle?: boolean;
}

function PricingTable() {
  const [isYearly, setIsYearly] = React.useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const PricingCard: React.FC<PricingCardProps> = ({
    plan,
    delay,
    className,
    isRecommended,
    isYearly,
    onBillingChange,
    showBillingToggle,
  }) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const isCardInView = useInView(cardRef, { once: true });

    return (
      <MotionDiv
        ref={cardRef}
        initial={isRecommended ? { opacity: 1 } : { opacity: 0 }}
        animate={isCardInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5, delay }}
        className={clsx(className, "relative grow")}
      >
        {isRecommended && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 rotate-[-10deg] whitespace-nowrap">
            <Badge className="bg-golden-500 text-white">Tired of subscriptions? Buy once & own forever</Badge>
          </div>
        )}
        <Card className={clsx("h-full shadow-lg", isRecommended && "border-golden-500 shadow-golden-200/50 shadow-xl")}>
          <CardHeader>
            {showBillingToggle && (
              <div className="mb-6 flex justify-center">
                <div className="bg-muted flex items-center space-x-2 rounded-full p-1.5">
                  <span
                    className={clsx(
                      "cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                      !isYearly
                        ? "bg-background text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => onBillingChange?.(false)}
                  >
                    Monthly
                  </span>
                  <span
                    className={clsx(
                      "cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                      isYearly
                        ? "bg-background text-foreground shadow-xs"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => onBillingChange?.(true)}
                  >
                    <span className="flex flex-wrap items-center gap-1.5">
                      Yearly
                      <span className="bg-primary/10 text-primary hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium sm:inline-flex sm:whitespace-nowrap">
                        🔥 Save $48 annually
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-xl">{plan.title}</h3>
              <div className="flex items-end gap-2">
                <div className="text-5xl font-bold tracking-tight">{plan.price}</div>
                <div className="text-muted-foreground mb-1 flex flex-col text-sm">
                  {!plan.isLifetime && isYearly && <span>billed annually</span>}
                  {!plan.isLifetime && <strong>per month</strong>}
                  {plan.isLifetime && <span>one-time payment</span>}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col justify-between">
            <ul className="mb-6 space-y-4">
              {plan.isLifetime ? (
                <li className="flex items-start gap-x-3">
                  <span className="flex h-[1lh] items-center">
                    <div className="flex size-5 items-center justify-center rounded-full border border-green-500/20 bg-green-500/20">
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    </div>
                  </span>
                  <span className="text-muted-foreground mt-0.5 text-sm">
                    All subscription features included forever
                  </span>
                </li>
              ) : (
                plan.features.map((feature: string, featureIndex: number) => (
                  <li key={featureIndex} className="flex items-start gap-x-3">
                    <span className="flex h-[1lh] items-center">
                      <div className="flex size-5 items-center justify-center rounded-full border border-green-500/20 bg-green-500/20 text-green-500">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                    </span>
                    <span className="text-muted-foreground mt-0.5 text-sm">{feature}</span>
                  </li>
                ))
              )}
              {!plan.isLifetime && (
                <li className="flex items-start gap-x-3">
                  <span className="flex h-[1lh] items-center">
                    <div className="border-golden-200 bg-golden-100 flex size-5 items-center justify-center rounded-full border">
                      <Clock className="text-golden-600 h-3.5 w-3.5" />
                    </div>
                  </span>
                  <span className="text-golden-600 mt-0.5 text-sm">More features coming soon</span>
                </li>
              )}
            </ul>
            <Link href="/sign_in" className="block w-full">
              <Button className="hover:bg-golden-400 bg-primary text-primary-foreground hover:text-primary-foreground w-full cursor-pointer transition-colors duration-150">
                Connect GitHub & {plan.isLifetime ? "Pay" : "Subscribe"} <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </MotionDiv>
    );
  };

  return (
    <MotionDiv
      ref={sectionRef}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mx-auto grid max-w-4xl grid-cols-1 place-items-end gap-8 lg:grid-cols-2">
        <div className="flex w-full flex-wrap">
          <PricingCard
            plan={{
              title: "Unlimited",
              price: isYearly ? "$25" : "$29",
              description: "One plan, everything you need.",
              features: [
                "Unlimited team members",
                "Unlimited apps",
                "Unlimited code change summaries",
                "Daily / weekly email digests",
                "Slack notifications",
                "App analytics and velocity trends",
                "24/7 support",
              ],
            }}
            delay={0}
            isYearly={isYearly}
            onBillingChange={setIsYearly}
            showBillingToggle={true}
          />
        </div>
        <div className="mt-8 flex w-full flex-col items-center gap-16 lg:mt-0 lg:items-start lg:gap-8">
          <svg
            width="122"
            height="97"
            viewBox="0 0 122 97"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="hidden text-gray-300 lg:block"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M116.102 96.4241C114.952 96.1896 112.7 94.9937 111.433 93.9854C110.869 93.5398 109.368 92.3674 108.077 91.4059C103.455 87.8885 102.61 87.1147 102.187 86.036C101.39 83.9255 102.798 81.9323 105.097 81.9323C106.13 81.9323 108.241 82.7296 109.696 83.6676C110.424 84.1366 111.01 84.4414 111.01 84.3711C111.01 83.8317 107.796 78.6963 106.2 76.7031C102.023 71.4504 95.6642 66.8309 86.2548 62.2348C81.0926 59.7023 77.4555 58.2484 73.9123 57.287C71.7066 56.7007 70.6507 56.5366 67.9053 56.4428C66.0516 56.3724 64.5499 56.349 64.5499 56.3959C64.5499 56.4428 64.808 57.5449 65.1365 58.8346C65.465 60.1244 65.8404 62.3521 66.0047 63.759C66.4505 68.1441 65.4884 72.2243 63.4704 74.2878C62.1564 75.6479 60.9363 76.1638 59.0121 76.1638C57.6043 76.1638 57.1115 76.07 55.7975 75.4134C52.8878 73.983 50.5648 70.5359 49.5089 66.104C48.453 61.6017 49.2742 58.436 52.3481 55.411C53.4744 54.3089 54.46 53.6054 55.9852 52.8316C57.1584 52.2688 58.1439 51.7764 58.1909 51.7529C58.5898 51.5184 54.5304 43.0532 52.0666 38.9026C47.4674 31.2112 39.3486 21.9487 30.5728 14.4448C22.2427 7.29281 16.7285 4.08021 9.87677 2.36841C8.28116 1.96976 7.13138 1.87596 4.2452 1.87596C1.17131 1.85251 0.608154 1.80561 0.608154 1.50071C0.608154 1.28971 1.19478 0.938014 2.13337 0.562814C3.54126 0.0469141 3.96363 -0.0234859 7.41296 0.000014141C10.5572 0.000014141 11.4724 0.0938141 13.1149 0.515914C21.7265 2.83741 31.1594 9.33291 42.6102 20.8231C49.2977 27.5062 52.5828 31.5864 56.1494 37.5894C58.0501 40.802 60.6312 45.8436 61.7575 48.5872L62.5553 50.5335L64.0806 50.3694C71.3547 49.619 77.7136 51.2136 88.3667 56.4897C96.2274 60.3823 101.976 64.1811 106.505 68.4489C108.617 70.4421 111.855 74.2409 112.794 75.812C113.028 76.2107 113.286 76.539 113.357 76.539C113.427 76.539 113.662 75.7417 113.873 74.8037C114.084 73.8423 114.647 72.2477 115.093 71.2628C115.82 69.7152 116.008 69.4807 116.454 69.551C116.876 69.5979 117.228 70.0904 117.956 71.5442C119.317 74.2409 119.833 76.2576 120.772 82.6358C121.757 89.272 121.781 92.1094 120.889 93.9619C119.95 95.9082 118.12 96.8462 116.102 96.4241ZM60.7016 70.747C61.4525 69.6214 61.8279 67.2295 61.6637 64.6032C61.4759 61.7424 60.5139 57.5449 60.0681 57.5449C59.5284 57.5449 57.1584 58.8815 56.2198 59.7023C54.8354 60.9216 54.3426 62.2348 54.5538 64.228C54.8589 67.2764 56.1964 70.3014 57.5808 71.169C58.7306 71.8725 60.0681 71.6849 60.7016 70.747Z"
              fill="currentColor"
            />
          </svg>
          <ArrowBottom className="lg:hidden" />
          <PricingCard
            plan={{
              title: "Lifetime",
              price: "$1,000",
              description: "One-time payment, lifetime access.",
              features: [],
              isLifetime: true,
            }}
            delay={0.2}
            isRecommended={true}
            isYearly={isYearly}
          />
        </div>
      </div>
    </MotionDiv>
  );
}

export default function PricingSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <Container className="py-24">
      <MotionDiv
        ref={sectionRef}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-24"
      >
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[#E6A91A] text-sm font-semibold tracking-wider uppercase">Get started</p>
          <h2 className="text-foreground mt-4 text-4xl tracking-tight sm:text-5xl lg:text-6xl">
            Ditch your standups and get your first code report
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-8 whitespace-pre-line">
            No price per-seat bullshit. One price for your entire organization—unlimited teammates, repositories, and
            code change summaries. Choose between a subscription or a one-time payment, both loaded with all the
            features to boost your team's productivity.
          </p>
        </div>
      </MotionDiv>
      <div className="mt-16 sm:mt-24">
        <PricingTable />
      </div>
    </Container>
  );
}
